/**
 * Content Analysis Transformer
 * Transforms WooRank keywords_cloud criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Generates a scenario-based message for content analysis
 * @param {Array} data - The keywords data array
 * @returns {string} - The appropriate message based on the scenario
 */
const generateMessage = (data = []) => {
  // No data scenario
  if (!data || data?.length === 0) {
    return 'No keywords or phrases were detected on this page.';
  }

  const count = data?.length || 0;
  // Data exists
  return `We found ${count} keyword${count === 1 ? '' : 's'}/phrase${count === 1 ? '' : 's'} on this page.`;
};

/**
 * Transforms the keywords_cloud criteria from WooRank API response
 * @param {Object} keywordsCriteria - The keywords_cloud criteria object from the API
 * @returns {Object} - Transformed content analysis data for React components
 */
export const transformContentAnalysis = (keywordsCriteria) => {
  // Handle missing criteria entirely
  if (!keywordsCriteria) {
    return {
      status: 'error',
      iconType: 'error',
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'Content analysis data is unavailable.',
      data: null,
    };
  }

  const data = keywordsCriteria?.data || [];
  const wooRankStatus = keywordsCriteria?.status || 'neutral';
  const importance = keywordsCriteria?.importance || 0;
  const solvability = keywordsCriteria?.solvability || 0;

  // Check if data is empty
  const isEmpty = !data || data?.length === 0;

  // Use API status directly - special scenarios only affect messages
  const status = getStatus(wooRankStatus);
  const iconType = getIconType(wooRankStatus);

  return {
    status,
    iconType,
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(data),
    data: isEmpty ? null : {
      keywords: (data || []).map((item) => ({
        keyword: item?.keyword || '',
        amount: item?.amount || 0,
      })),
    },
  };
};

export default transformContentAnalysis;

